<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <Teleport to="body">
    <transition
      @after-enter="showContent = true"
      @after-leave="onAfterLeaveModal"
    >
      <div
        v-if="show"
        v-global-keydown:Escape="closeByESC"
        class="modal js-modal"
        :class="className"
      >
        <transition name="fade" @after-leave="show = false">
          <div
            v-if="showContent"
            class="modal__backdrop"
            @click="closeByClickOutside ? closeModal() : null"
          ></div>
        </transition>
        <transition :name="transition">
          <div v-if="showContent" class="modal__dialog">
            <div
              v-if="$slots.header"
              ref="modalHeader"
              class="modal__header"
              :class="classes.header"
            >
              <slot name="header" />
              <slot name="close-btn">
                <button
                  type="button"
                  class="modal__close"
                  @click="closeModal()"
                >
                  <ButtonClose />
                </button>
              </slot>
            </div>

            <div class="modal__body" :class="classes.body">
              <slot name="body" />
            </div>

            <div>
              <slot name="footer" />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watchEffect } from 'vue';

import ButtonClose from '@/shared/components/UI/Buttons/ButtonClose.vue';
import { useSwipe } from '@/shared/composables/useSwipe';
import { useConfigStore } from '@/shared/pinia/modules/config';

const modalHeader = ref(null);
const show = ref(false);
const showContent = ref(false);
const anotherModalOpened = ref(false);
const alreadyClosed = ref(false);
const scrollableElementSelector = '.modal__body';
const emit = defineEmits(['onClose', 'onOpen']);
const configStore = useConfigStore();

const props = defineProps({
  className: {
    type: String,
    default: ''
  },
  classes: {
    type: Object,
    default: () => ({
      header: '',
      body: ''
    })
  },
  transition: {
    type: String,
    default: 'fade'
  },
  closeBySwipeHeader: {
    type: Boolean,
    default: false
  },
  closeByClickOutside: {
    type: Boolean,
    default: true
  }
});

watchEffect(() => {
  if (showContent.value) {
    nextTick(() => {
      const elementExists = document.querySelector(scrollableElementSelector);

      if (elementExists) {
        configStore.lockBodyScroll(scrollableElementSelector);
      }
    });
  }
});

const openModal = () => {
  alreadyClosed.value = false;
  show.value = true;
  emit('onOpen');

  if (configStore.isBodyScrollLocked) {
    anotherModalOpened.value = true;
  }
};

const closeByESC = () => {
  closeModal();
};

const closeModal = (byPassCallback) => {
  showContent.value = false;

  if (!byPassCallback) {
    emit('onClose');
  }

  if (!anotherModalOpened.value) {
    alreadyClosed.value = true;
    configStore.allowBodyScroll();
    anotherModalOpened.value = false;
  }
};

const onAfterLeaveModal = () => {
  if (!anotherModalOpened.value && !alreadyClosed.value) {
    configStore.allowBodyScroll();
  }
};

const modalHeading = computed(
  () => props.closeBySwipeHeader && modalHeader.value
);
const { onSwipeDown } = useSwipe(modalHeading);

onSwipeDown(() => {
  closeModal();
});

onUnmounted(() => {
  configStore.clearAllBodyScrollLocks();
});

defineExpose({
  openModal,
  closeModal,
  showContent
});
</script>

<style lang="scss" scoped>
.modal {
  overflow-x: hidden;
  overflow-y: hidden;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1039;
  display: flex;

  &__backdrop {
    background-color: rgba(0, 0, 0, 0.45);
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
  }
  &__dialog {
    background-color: #ffffff;
    position: relative;
    margin: auto;
    width: 90%;
    max-width: 625px;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    z-index: 2;
    overflow: hidden;
    max-height: 100vh;
  }
  &__close {
    cursor: pointer;
    border-radius: 50px;
    width: 40px;
    height: 40px;
    position: absolute;
    right: 20px;
    line-height: 40px;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 68px;

    :deep(h1) {
      margin: 0;
      font-size: 20px;
      color: #28282b;
    }
  }
  &__body {
    overflow: auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
}

.modal.options-modal {
  .modal__dialog {
    width: 100%;
    max-width: 100%;
    max-height: 80vh;
    height: 98vh;
    bottom: 0;
    position: absolute;
    border-radius: 0;

    @media screen and (min-width: 768px) {
      box-shadow: rgb(0 0 0 / 28%) 0px 8px 28px;
      max-height: 90vh;
      height: auto;
      position: relative;
      bottom: auto;
      border-radius: 12px;
      width: 90%;
      max-width: 1030px;
    }
  }

  .modal__close {
    border: none;
    background-color: transparent;
    border-radius: 8px;

    &:hover {
      background-color: #f2f2f2;
    }
  }

  .modal__header {
    border-bottom: 1px solid #c6c6c8;
    justify-content: flex-start;
    padding: 0 18px;
    height: 64px;
    min-height: 64px;

    @media screen and (min-width: 768px) {
      padding: 0 38px;
      height: 62px;
      min-height: 62px;
    }

    :deep(h1) {
      color: #222;
      font-weight: 700;
      white-space: nowrap;
    }
  }
}

.modal.notifications-modal {
  justify-content: flex-end;

  .modal__dialog {
    height: 100vh;
    width: 90vw;
    max-width: 500px;
    border-radius: 0;
    margin: inherit;
  }

  .modal__close {
    border: none;
  }

  .modal__header {
    justify-content: flex-start;
    padding-left: 16px;
    border-bottom: 1px solid #f2f2f2;
    height: 64px;
    min-height: 64px;
  }
}

.modal.toc-confirmation-modal {
  z-index: 1040;

  .modal__dialog {
    width: 96vw;
    max-width: 360px;
    background: rgb(215, 245, 250);
    background:
      url('data:image/svg+xml;base64,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')
        no-repeat scroll right top,
      linear-gradient(
        180deg,
        rgba(215, 245, 250, 1) 0%,
        rgba(255, 255, 255, 1) 100%
      );
    background-size: 190px, cover;
  }

  .modal__close {
    border: none;
    background-color: #b4ebf0;
    :deep(line) {
      stroke: #00b9cd;
    }
  }

  .modal__body {
    padding: 0 24px 24px;
  }
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-bottom-fade-move,
.slide-bottom-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-bottom-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-bottom-fade-enter-from,
.slide-bottom-fade-leave-to {
  transform: translateY(500px);
  opacity: 0;
}

.slide-right-fade-move,
.slide-right-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-right-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-right-fade-enter-from,
.slide-right-fade-leave-to {
  transform: translateX(500px);
  opacity: 0;
}
</style>

<style>
.overflow-hidden {
  overflow: hidden;
}
</style>
